import _styled, { default as _styled2, default as _styled3 } from "styled-components";
const _excluded = ["style", "className", "children", "variant", "now", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import styledC, { css, keyframes } from 'styled-components';
import { useInstanceId } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
const spin = keyframes(["100%{transform:rotate(360deg);}"]);
const spinAnimation = css(["animation:", " 0.6s linear infinite;"], spin);
const PROGRESS_RING_SIZES = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge'
};
const PROGRESS_RING_VARIANTS = {
  DEFAULT: 'default',
  ALTERNATIVE: 'alternative'
};
const mapConstToSize = {
  [PROGRESS_RING_SIZES.XLARGE]: 216,
  [PROGRESS_RING_SIZES.LARGE]: 128,
  [PROGRESS_RING_SIZES.MEDIUM]: 64,
  [PROGRESS_RING_SIZES.SMALL]: 24,
  [PROGRESS_RING_SIZES.XSMALL]: 16
};
const StyledDiv = styledC.div.withConfig({
  displayName: "ProgressRing__StyledDiv",
  componentId: "sc-2pm30z-0"
})(["display:inline-block;color:", ";width:", "px;height:", "px;transform:rotate(", "deg);", ""], props => props.$variant === PROGRESS_RING_VARIANTS.ALTERNATIVE ? theme.tokens.colors.icon.inverse.value : theme.tokens.colors.icon.load.value, props => props.$size, props => props.$size, props => props.$indeterminate ? 0 : -90, props => props.$indeterminate ? spinAnimation : '');
var _StyledStyledDiv = styledC(StyledDiv).withConfig({
  displayName: "ProgressRing___StyledStyledDiv",
  componentId: "sc-2pm30z-1"
})(["", ""], p => p.$_css);
const StyledProgressCircle = styledC.circle.attrs(props => {
  var _props$$now;
  return {
    style: {
      strokeDashoffset: props.$indeterminate ? props.$fixedCircumference / 2 : `${(100 - Math.min((_props$$now = props.$now) !== null && _props$$now !== void 0 ? _props$$now : 0, 100)) / 100 * props.$fixedCircumference}px`,
      stroke: props.$indeterminate ? props.$alternative ? `url(#${props.$instanceId}-indeterminateWhite)` : `url(#${props.$instanceId}-indeterminateBlue)` : 'currentColor',
      strokeLinecap: 'round',
      strokeDasharray: props.$fixedCircumference,
      transition: props.$indeterminate ? 'none' : 'stroke-dashoffset linear 0.2s'
    }
  };
}).withConfig({
  displayName: "ProgressRing__StyledProgressCircle",
  componentId: "sc-2pm30z-2"
})([""]);

/**
 * ProgressRings can be used to show the loading state in your projects.
 */
const ProgressRing = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      children: _children,
      variant = PROGRESS_RING_VARIANTS.DEFAULT,
      now,
      size = PROGRESS_RING_SIZES.SMALL
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const sizeInPixels = mapConstToSize[size];
  const THICKNESS = size === PROGRESS_RING_SIZES.XSMALL ? 2 : 4;
  const circumference = 2 * Math.PI * ((sizeInPixels - THICKNESS) / 2);
  const fixedCircumference = +circumference.toFixed(3);
  const circleProps = {
    cx: sizeInPixels,
    cy: sizeInPixels,
    r: (sizeInPixels - THICKNESS) / 2,
    fill: 'none',
    strokeWidth: THICKNESS
  };
  const indeterminate = isNaN(now);
  const alternative = variant === PROGRESS_RING_VARIANTS.ALTERNATIVE;
  const instanceId = `alloy-progress-ring-${useInstanceId()}`;
  return /*#__PURE__*/React.createElement(_StyledStyledDiv, _extends({
    role: "progressbar",
    "aria-label": "progressbar",
    "aria-valuetext": indeterminate ? 'indeterminate' : undefined,
    "aria-valuenow": indeterminate ? undefined : now,
    "aria-valuemin": 0,
    "aria-valuemax": 100
  }, props, {
    className: className,
    ref: ref,
    $indeterminate: indeterminate,
    $variant: variant,
    $size: sizeInPixels,
    $_css: style
  }), /*#__PURE__*/React.createElement(_StyledSvg, {
    viewBox: `${sizeInPixels / 2} ${sizeInPixels / 2} ${sizeInPixels} ${sizeInPixels}`,
    $_css2: [{
      display: 'block'
    }]
  }, /*#__PURE__*/React.createElement("defs", {
    role: "presentation"
  }, /*#__PURE__*/React.createElement("linearGradient", {
    gradientTransform: "rotate(270)",
    id: `${instanceId}-indeterminateBlue`,
    x1: "50%",
    y1: "100%",
    x2: "50%",
    y2: "0%"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: "0%",
    stopOpacity: "0",
    stopColor: theme.tokens.colors.icon.load.value
  }), /*#__PURE__*/React.createElement("stop", {
    offset: "100%",
    stopOpacity: "1",
    stopColor: theme.tokens.colors.icon.load.value
  })), /*#__PURE__*/React.createElement("linearGradient", {
    gradientTransform: "rotate(270)",
    id: `${instanceId}-indeterminateWhite`,
    x1: "50%",
    y1: "100%",
    x2: "50%",
    y2: "0%"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: "0%",
    stopOpacity: "0",
    stopColor: theme.tokens.colors.icon.inverse.value
  }), /*#__PURE__*/React.createElement("stop", {
    offset: "100%",
    stopOpacity: "1",
    stopColor: theme.tokens.colors.icon.inverse.value
  }))), /*#__PURE__*/React.createElement(_StyledCircle, _extends({}, circleProps, {
    $_css3: [{
      stroke: alternative ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, .05)'
    }]
  })), /*#__PURE__*/React.createElement(StyledProgressCircle, _extends({}, circleProps, {
    $alternative: alternative,
    $indeterminate: indeterminate,
    $instanceId: instanceId,
    $fixedCircumference: fixedCircumference,
    $now: now
  }))));
});
ProgressRing.displayName = 'ProgressRing';
ProgressRing.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The component's children */
  children: PropTypes.any,
  /** Current ProgressRing value, if omitted would animate automatically. */
  now: PropTypes.number,
  /** The ProgressRing variant */
  variant: PropTypes.oneOf(Object.values(PROGRESS_RING_VARIANTS)),
  /**  The size of the ProgressRing, defaults to 'small' */
  size: PropTypes.oneOf(Object.values(PROGRESS_RING_SIZES))
};
export default Object.assign(ProgressRing, {
  SIZES: PROGRESS_RING_SIZES,
  VARIANTS: PROGRESS_RING_VARIANTS
});
var _StyledSvg = styledC("svg").withConfig({
  displayName: "ProgressRing___StyledSvg",
  componentId: "sc-2pm30z-3"
})(["", ""], p => p.$_css2);
var _StyledCircle = styledC("circle").withConfig({
  displayName: "ProgressRing___StyledCircle",
  componentId: "sc-2pm30z-4"
})(["", ""], p => p.$_css3);
//# sourceMappingURL=ProgressRing.js.map